import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IconInput } from 'app/shared/input';
import { Checkbox } from 'app/shared/input/checkbox';
import { MultiSelect } from 'app/shared/select';
import { DetailItem } from 'app/shared/info-section/item';
import { GridColumn, GridRow } from 'app/layouts/grid';
import GmModal from 'app/shared/modal/modal';
import useValidator from 'hooks/validator';
import { useAgencyService } from 'hooks/iam/agency';
import { agencyValidationConfig } from 'app/shared/utils/form/validation/agency';
import { FlowShell } from '../components/flow-shell';
import { VerifiableField } from '../components/verifiable-field';
import { VerifyPhoneModal } from '../verify-phone-modal';
import { usePhoneVerifier } from '../hooks/use-phone-verifier';
import { useVerifier } from '../hooks/use-verifier';
import { COUNTRY_OPTIONS, PHONE_MAX_ATTEMPTS, BVN_MAX_ATTEMPTS, NIN_MAX_ATTEMPTS } from '../data';
import styles from '../create.module.css';

const STEPS = ['Details', 'Verification'];
const SUBTITLES = [
  'Tell us who you are and where you are based.',
  'A few details to verify your identity and keep payouts secure.'
];

export const FreelancerFlow = ({ onCreated, onBack }) => {
  const { email: user_email } = useSelector((state) => state.user_data);
  const existing_agency = useSelector((state) => state.metadata.agency);
  const { createAgency, updateAgency, managePhoneVerification, verifyBVN, verifyNIN } =
    useAgencyService();
  const { errors, validateField } = useValidator(agencyValidationConfig);

  const [screen, setScreen] = useState(1);
  const [agency, setAgency] = useState(null);
  const [resumed, setResumed] = useState(false);
  const [selected_country, setSelectedCountry] = useState(null);
  const [attest_info, setAttestInfo] = useState(false);
  const [attest_not_employee, setAttestNotEmployee] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form_data, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    state: '',
    street: '',
    zip: '',
    phone_number: '',
    bvn: '',
    nin: ''
  });

  // Attempt counts persisted on the agency record, used to seed the verifiers
  // so the remaining-attempts indicator and lock state survive a reload.
  const attempts_seed = existing_agency?.verification_attempts || {};

  const phone = usePhoneVerifier({
    maxAttempts: PHONE_MAX_ATTEMPTS,
    initialAttempts: attempts_seed.phone_number || 0,
    initiate: (value) =>
      managePhoneVerification('initiation', {
        data: { agency_id: agency?.id, phone_number: value }
      }),
    confirm: (value, code) =>
      managePhoneVerification('confirmation', { data: { phone_number: value, otp: code } })
  });
  const bvn = useVerifier({
    maxAttempts: BVN_MAX_ATTEMPTS,
    initialAttempts: attempts_seed.bvn || 0,
    verify: () => verifyBVN({ data: { agency_id: agency?.id, bvn: form_data.bvn } })
  });
  const nin = useVerifier({
    maxAttempts: NIN_MAX_ATTEMPTS,
    initialAttempts: attempts_seed.nin || 0,
    verify: () => verifyNIN({ data: { agency_id: agency?.id, nin: form_data.nin } })
  });

  // Resume an unfinished registration: pre-fill and jump to verification.
  useEffect(() => {
    if (!existing_agency || !existing_agency.id) return;

    // Adopt the existing record so switching tracks updates it (flipping `track`)
    // instead of creating a duplicate. Only pre-fill when it's already a freelancer.
    setAgency(existing_agency);
    if (existing_agency.track !== 'freelancer') return;

    setResumed(true);
    const address = existing_agency.address || {};
    const attestations = existing_agency.attestations || {};
    const status = existing_agency.verification_status || {};

    setFormData((prev) => ({
      ...prev,
      firstname: existing_agency.firstname || '',
      lastname: existing_agency.lastname || '',
      email: existing_agency.email || '',
      state: address.state || '',
      street: address.street || '',
      zip: address.zip || '',
      phone_number: existing_agency.phone_number || '',
      bvn: existing_agency.bvn || '',
      nin: existing_agency.nin || ''
    }));

    if (address.country) {
      const option = COUNTRY_OPTIONS.find(
        (o) => o.value === address.country || o.label === address.country
      );
      if (option) setSelectedCountry(option);
    }

    setAttestInfo(!!attestations.information_is_correct);
    setAttestNotEmployee(!!attestations.not_client_employee);

    if (status.phone_number && existing_agency.phone_number)
      phone.setVerifiedValue(existing_agency.phone_number);
    if (status.bvn && existing_agency.bvn) bvn.setVerifiedValue(existing_agency.bvn);
    if (status.nin && existing_agency.nin) nin.setVerifiedValue(existing_agency.nin);

    setScreen(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    validateField(field, value);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const details_valid =
    !!form_data.firstname &&
    !errors.firstname &&
    !!form_data.lastname &&
    !errors.lastname &&
    !!form_data.email &&
    !errors.email &&
    !!selected_country &&
    !!form_data.state &&
    !!form_data.street &&
    !errors.street &&
    !!form_data.zip &&
    !errors.zip;

  const phone_is_valid = !!form_data.phone_number && !errors.phone_number;
  const bvn_is_valid = /^\d{11}$/.test(form_data.bvn);
  const nin_is_valid = /^\d{11}$/.test(form_data.nin);

  const phone_verified = phone.isVerified(form_data.phone_number);
  const bvn_verified = bvn.isVerified(form_data.bvn);
  const nin_verified = nin.isVerified(form_data.nin);

  const verification_valid =
    phone_verified && bvn_verified && nin_verified && attest_info && attest_not_employee;

  const saveDetails = async () => {
    if (!details_valid) {
      if (resumed) setScreen(2);
      return;
    }

    const data = {
      track: 'freelancer',
      firstname: form_data.firstname,
      lastname: form_data.lastname,
      email: form_data.email,
      address: {
        country: selected_country.value,
        state: form_data.state,
        street: form_data.street,
        zip: form_data.zip
      },
      user_email
    };

    setSaving(true);
    if (agency) {
      const { error } = await updateAgency(agency.id, { data });
      setSaving(false);
      if (!error) setScreen(2);
      return;
    }

    const { agency: record } = await createAgency({ data });
    setSaving(false);
    if (record && record.id) {
      setAgency(record);
      setScreen(2);
    }
  };

  const submitForm = async () => {
    const data = {
      phone_number: form_data.phone_number,
      bvn: form_data.bvn,
      nin: form_data.nin,
      attestations: {
        information_is_correct: attest_info,
        not_client_employee: attest_not_employee
      },
      registration_completed: true
    };

    setSaving(true);
    const { error } = await updateAgency(agency.id, { data });
    setSaving(false);
    if (!error) onCreated({ ...agency, ...data });
  };

  const is_last = screen === STEPS.length;

  return (
    <FlowShell
      steps={STEPS}
      current={screen}
      title="Create your agency"
      subtitle={SUBTITLES[screen - 1]}
      onBack={() => (screen === 1 ? onBack() : setScreen(1))}
      primaryLabel={is_last ? 'Create agency →' : 'Continue →'}
      onPrimary={is_last ? submitForm : saveDetails}
      primaryDisabled={is_last ? !verification_valid : !(details_valid || resumed)}
      busy={saving}
      modal={
        <GmModal
          show_title
          title="Verify your phone number"
          show_modal={phone.showModal}
          onClose={() => phone.setShowModal(false)}
        >
          <VerifyPhoneModal
            phone_number={form_data.phone_number}
            confirming={phone.confirming}
            resending={phone.requesting}
            attemptsLeft={phone.attemptsLeft}
            onConfirm={(code) => phone.submitCode(form_data.phone_number, code)}
            onResend={() => phone.request(form_data.phone_number)}
          />
        </GmModal>
      }
    >
      {screen === 1 && (
        <>
          <GridRow num_of_columns={2}>
            <GridColumn>
              <DetailItem dark title="First Name" required>
                <IconInput
                  dark
                  input_id="firstname"
                  value={form_data.firstname}
                  onInput={(value) => handleChange('firstname', value)}
                  error={errors.firstname}
                />
              </DetailItem>
            </GridColumn>
            <GridColumn>
              <DetailItem dark title="Last Name" required>
                <IconInput
                  dark
                  input_id="lastname"
                  value={form_data.lastname}
                  onInput={(value) => handleChange('lastname', value)}
                  error={errors.lastname}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem dark title="Email" required>
                <IconInput
                  dark
                  type="email"
                  input_id="email"
                  value={form_data.email}
                  onInput={(value) => handleChange('email', value)}
                  error={errors.email}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={4}>
            <GridColumn span={2}>
              <DetailItem dark title="Country" required>
                <MultiSelect
                  dark
                  input_id="country"
                  options={COUNTRY_OPTIONS}
                  value={selected_country}
                  onChange={setSelectedCountry}
                  placeholder="Select country"
                />
              </DetailItem>
            </GridColumn>
            <GridColumn span={1}>
              <DetailItem dark title="State" required>
                <IconInput
                  dark
                  input_id="state"
                  value={form_data.state}
                  onInput={(value) => handleChange('state', value)}
                />
              </DetailItem>
            </GridColumn>
            <GridColumn span={1}>
              <DetailItem dark title="Zip / Postal Code" required>
                <IconInput
                  dark
                  input_id="zip"
                  value={form_data.zip}
                  onInput={(value) => handleChange('zip', value)}
                  error={errors.zip}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={1}>
            <GridColumn>
              <DetailItem dark title="Street Address" required>
                <IconInput
                  dark
                  input_id="street"
                  value={form_data.street}
                  onInput={(value) => handleChange('street', value)}
                  error={errors.street}
                />
              </DetailItem>
            </GridColumn>
          </GridRow>
        </>
      )}

      {screen === 2 && (
        <>
          <GridRow num_of_columns={1}>
            <GridColumn>
              <VerifiableField
                title="Phone Number"
                value={form_data.phone_number}
                onChange={(value) => handleChange('phone_number', value)}
                error={errors.phone_number}
                verified={phone_verified}
                verifying={phone.requesting}
                verifyingText="Sending code…"
                verifyLabel="Click to verify phone number"
                onVerify={() => phone.request(form_data.phone_number)}
                showVerify={phone_is_valid}
                locked={phone.locked}
                attemptsLeft={phone.attemptsLeft}
              />
            </GridColumn>
          </GridRow>
          <GridRow num_of_columns={2}>
            <GridColumn>
              <VerifiableField
                title="BVN"
                type="number"
                value={form_data.bvn}
                onChange={(value) => handleChange('bvn', value)}
                error={errors.bvn}
                verified={bvn_verified}
                verifying={bvn.verifying}
                verifyLabel="Click to verify BVN"
                onVerify={() => bvn.run(form_data.bvn)}
                showVerify={bvn_is_valid}
                locked={bvn.locked}
                attemptsLeft={bvn.attemptsLeft}
              />
            </GridColumn>
            <GridColumn>
              <VerifiableField
                title="NIN"
                type="number"
                value={form_data.nin}
                onChange={(value) => handleChange('nin', value)}
                error={errors.nin}
                verified={nin_verified}
                verifying={nin.verifying}
                verifyLabel="Click to verify NIN"
                onVerify={() => nin.run(form_data.nin)}
                showVerify={nin_is_valid}
                locked={nin.locked}
                attemptsLeft={nin.attemptsLeft}
              />
            </GridColumn>
          </GridRow>

          <div className={styles.attestations}>
            <Checkbox
              input_id="attest_info"
              checked={attest_info}
              onClick={() => setAttestInfo((prev) => !prev)}
              label="I confirm that the information supplied above is correct and accurate."
            />
            <Checkbox
              input_id="attest_not_employee"
              checked={attest_not_employee}
              onClick={() => setAttestNotEmployee((prev) => !prev)}
              label="I confirm that I am not an employee of any of the clients I onboard."
            />
          </div>
        </>
      )}
    </FlowShell>
  );
};
